"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = NewCactusCartValidation;
var _react = _interopRequireWildcard(require("react"));
var _newcactusApi = require("../../service/newcactusApi");
var _newcactusMenuUtils = require("../../service/newcactusMenuUtils");
var _terminalBridge = require("../../service/terminalBridge");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
const QR_CODE_SIZE = 280;
const IMMEDIATE_METHODS = new Set(['cash', 'card', 'external']);
function buildOrderItems(cart) {
  return cart.map(item => (0, _newcactusMenuUtils.buildOrderLineFromCartItem)(item));
}
function buildSyncLines(cart) {
  return cart.map(item => ({
    menu_item_id: item.menuItemId,
    name: item.productName,
    quantity: item.quantity,
    unit_price: item.unitPrice,
    options: item.optionLabels ?? [],
    removed: item.removedLabels ?? []
  }));
}
function buildPaymentPayload(method, phone, stripePaymentIntentId) {
  if (method === _terminalBridge.STRIPE_WISEPAD_METHOD) {
    return {
      method: 'external',
      ...(stripePaymentIntentId ? {
        external_ref: stripePaymentIntentId
      } : {})
    };
  }
  const payment = {
    method
  };
  if (method === 'momo') payment.momo_phone = phone.trim();
  if (method === 'airtel') payment.airtel_phone = phone.trim();
  return payment;
}
function needsPhone(method) {
  return method === 'momo' || method === 'airtel';
}
function isAsyncMethod(method, terminalMode) {
  if (method === 'momo' || method === 'airtel' || method === 'online') return true;
  if (method === 'terminal' && terminalMode === 'cloud') return true;
  return false;
}
function formatCartLineName(item) {
  let name = item.productName;
  if (item.optionLabels?.length) name += ` (${item.optionLabels.join(', ')})`;
  if (item.removedLabels?.length) name += ` [sans ${item.removedLabels.join(', ')}]`;
  return name;
}
function buildAvailableMethods(ncMethods = []) {
  const base = [...(ncMethods ?? [])];
  if ((0, _terminalBridge.hasNativeTerminal)() && !base.includes(_terminalBridge.STRIPE_WISEPAD_METHOD)) {
    return [_terminalBridge.STRIPE_WISEPAD_METHOD, ...base];
  }
  return base;
}
function NewCactusCartValidation({
  cart,
  cartTotal,
  projectId,
  currency: currencyProp,
  ncConfig: ncConfigProp,
  userDatas = {},
  labels = {},
  paymentMethod: defaultPaymentMethod = 'cash',
  onBack,
  onProceedToCheckout
}) {
  const [config, setConfig] = (0, _react.useState)(ncConfigProp ?? null);
  const [configError, setConfigError] = (0, _react.useState)(null);
  const [loadingConfig, setLoadingConfig] = (0, _react.useState)(!ncConfigProp);
  const [paymentMethod, setPaymentMethod] = (0, _react.useState)(defaultPaymentMethod);
  const [phone, setPhone] = (0, _react.useState)('');
  const [loading, setLoading] = (0, _react.useState)(false);
  const [error, setError] = (0, _react.useState)(null);
  const [waitingMessage, setWaitingMessage] = (0, _react.useState)(null);
  const [checkoutUrl, setCheckoutUrl] = (0, _react.useState)(null);
  const [waitingReaderPayment, setWaitingReaderPayment] = (0, _react.useState)(false);
  const wisepadPendingRef = (0, _react.useRef)(false);
  (0, _react.useEffect)(() => {
    if (ncConfigProp) {
      setConfig(ncConfigProp);
      setLoadingConfig(false);
      return;
    }
    if (!projectId) return;
    let cancelled = false;
    setLoadingConfig(true);
    (0, _newcactusApi.fetchNewCactusConfig)(projectId).then(data => {
      if (cancelled) return;
      setConfig(data);
      const methods = buildAvailableMethods(data.payment_methods);
      if (methods.length && !methods.includes(defaultPaymentMethod)) {
        setPaymentMethod(methods.includes(_terminalBridge.STRIPE_WISEPAD_METHOD) ? _terminalBridge.STRIPE_WISEPAD_METHOD : methods[0]);
      }
    }).catch(err => {
      if (!cancelled) setConfigError(err.message || 'Impossible de charger la config New Cactus');
    }).finally(() => {
      if (!cancelled) setLoadingConfig(false);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, defaultPaymentMethod, ncConfigProp]);
  const currency = currencyProp ?? config?.currency ?? 'RWF';
  const formatPrice = value => (0, _newcactusApi.formatCurrency)(value, currency);
  const availableMethods = (0, _react.useMemo)(() => buildAvailableMethods(config?.payment_methods), [config?.payment_methods]);
  const terminalMode = config?.terminal?.mode ?? null;
  const finalizeSuccess = (0, _react.useCallback)(async (orderResponse, stripePaymentIntentId) => {
    const reference = orderResponse?.order?.reference;
    const receiptUrl = orderResponse?.receipt?.receipt_url ?? '';
    try {
      await (0, _newcactusApi.syncNewCactusLocalCommand)(projectId, {
        user_datas: {
          ...userDatas,
          ...(stripePaymentIntentId ? {
            stripe_payment_intent_id: stripePaymentIntentId
          } : {})
        },
        newcactus_reference: reference,
        receipt_url: receiptUrl,
        lines: buildSyncLines(cart)
      });
    } catch (syncErr) {
      console.error('Sync commande locale:', syncErr);
    }
    if (receiptUrl) {
      onProceedToCheckout?.({
        kind: 'receipt',
        receiptUrl,
        newcactusReference: reference
      });
    } else {
      onProceedToCheckout?.({
        kind: 'success',
        newcactusReference: reference
      });
    }
  }, [cart, onProceedToCheckout, projectId, userDatas]);
  const submitOrderToNewCactus = (0, _react.useCallback)(async stripePaymentIntentId => {
    const items = buildOrderItems(cart);
    const effectiveMethod = paymentMethod === _terminalBridge.STRIPE_WISEPAD_METHOD ? _terminalBridge.STRIPE_WISEPAD_METHOD : paymentMethod;
    const payload = {
      customer_name: userDatas.customer_name || 'Client borne',
      platform: 'Pluracore Borne',
      items,
      payment: buildPaymentPayload(effectiveMethod, phone, stripePaymentIntentId)
    };
    const orderResponse = await (0, _newcactusApi.createNewCactusOrder)(projectId, payload);
    const paymentStatus = orderResponse?.payment?.status;
    const reference = orderResponse?.order?.reference;
    if (paymentStatus === 'settled') {
      await finalizeSuccess(orderResponse, stripePaymentIntentId);
      return;
    }
    if (paymentMethod === 'online' && orderResponse?.payment?.checkout_url) {
      setCheckoutUrl(orderResponse.payment.checkout_url);
    }
    const asyncFlow = isAsyncMethod(paymentMethod, terminalMode);
    if (!asyncFlow || !reference) {
      if (IMMEDIATE_METHODS.has(paymentMethod) || paymentMethod === _terminalBridge.STRIPE_WISEPAD_METHOD) {
        await finalizeSuccess(orderResponse, stripePaymentIntentId);
      } else {
        throw new Error('Statut de paiement inattendu');
      }
      return;
    }
    const waitingByMethod = {
      momo: 'Validez la demande MTN MoMo sur votre téléphone (code PIN)…',
      airtel: 'Validez la demande Airtel Money sur votre téléphone…',
      terminal: 'Présentez votre carte sur le terminal…',
      online: 'Scannez le QR code et payez sur votre téléphone…'
    };
    setWaitingMessage(waitingByMethod[paymentMethod] || 'Paiement en attente…');
    const pollResult = await (0, _newcactusApi.pollNewCactusOrder)(projectId, reference, {
      onTick: data => {
        if (paymentMethod === 'online' && data?.payment?.status === 'settled') {
          void (0, _newcactusApi.confirmNewCactusOrder)(projectId, reference).catch(() => {});
        }
      }
    });
    if (pollResult.ok) {
      await finalizeSuccess(pollResult.data, stripePaymentIntentId);
    } else {
      throw new Error(pollResult.error || 'Paiement non confirmé');
    }
  }, [cart, finalizeSuccess, paymentMethod, phone, projectId, terminalMode, userDatas]);
  (0, _react.useEffect)(() => {
    const onNativeResult = ev => {
      if (!wisepadPendingRef.current) return;
      void (async () => {
        const d = (0, _terminalBridge.parseTerminalEventDetail)(ev.detail);
        setLoading(false);
        setWaitingReaderPayment(false);
        wisepadPendingRef.current = false;
        if (d.status === 'success') {
          try {
            setWaitingMessage('Envoi de la commande en cuisine…');
            await submitOrderToNewCactus(d.payment_intent_id);
          } catch (err) {
            setError(err?.message || 'Erreur après paiement carte');
          } finally {
            setWaitingMessage(null);
          }
          return;
        }
        if (d.status === 'cancelled') {
          setError(d.message || 'Paiement annulé sur le terminal. Vous pouvez réessayer ou modifier votre panier.');
          return;
        }
        setError(d.message || 'Paiement carte refusé ou erreur technique');
      })();
    };
    window.addEventListener('pluracore-terminal-payment', onNativeResult);
    document.addEventListener('pluracore-terminal-payment', onNativeResult);
    return () => {
      window.removeEventListener('pluracore-terminal-payment', onNativeResult);
      document.removeEventListener('pluracore-terminal-payment', onNativeResult);
    };
  }, [submitOrderToNewCactus]);
  const handleConfirmAndPay = (0, _react.useCallback)(async () => {
    if (!projectId || cart.length === 0) return;
    if (needsPhone(paymentMethod) && !phone.trim()) {
      setError('Veuillez saisir le numéro de téléphone');
      return;
    }
    setLoading(true);
    setError(null);
    setWaitingMessage(null);
    setCheckoutUrl(null);
    if (paymentMethod === _terminalBridge.STRIPE_WISEPAD_METHOD) {
      if (!(0, _terminalBridge.hasNativeTerminal)()) {
        setError('Terminal WisePad non disponible sur cet appareil');
        setLoading(false);
        return;
      }
      const amountCents = (0, _terminalBridge.toStripeMinorUnits)(cartTotal, currency);
      if (!amountCents) {
        setError('Montant invalide pour le paiement carte');
        setLoading(false);
        return;
      }
      wisepadPendingRef.current = true;
      setWaitingReaderPayment(true);
      const started = (0, _terminalBridge.startNativeTerminalPayment)({
        projectId,
        amountCents,
        currency: currency.toLowerCase()
      });
      if (!started) {
        wisepadPendingRef.current = false;
        setWaitingReaderPayment(false);
        setError('Impossible de démarrer le terminal carte');
        setLoading(false);
      }
      return;
    }
    try {
      await submitOrderToNewCactus();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la commande New Cactus');
    } finally {
      setLoading(false);
      setWaitingMessage(null);
    }
  }, [cart.length, cartTotal, currency, paymentMethod, phone, projectId, submitOrderToNewCactus]);
  if (!cart?.length) {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: "cart-validation",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "cart-validation__empty",
        children: "Panier vide"
      }), onBack && /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
        type: "button",
        className: "cart-validation__back",
        onClick: onBack,
        children: "Retour"
      })]
    });
  }
  if (loadingConfig) {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      className: "cart-validation",
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "cart-validation__empty",
        children: "Chargement des moyens de paiement\u2026"
      })
    });
  }
  if (configError || !config?.configured) {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: "cart-validation",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "cart-validation__error",
        role: "alert",
        children: configError || 'New Cactus non configuré pour ce projet'
      }), onBack && /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
        type: "button",
        className: "cart-validation__back",
        onClick: onBack,
        children: "Retour"
      })]
    });
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    className: "cart-validation",
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("header", {
      className: "cart-validation__header",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("h2", {
        className: "cart-validation__title",
        children: labels.recap_title || 'Récapitulatif'
      }), onBack && /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
        type: "button",
        className: "cart-validation__back",
        disabled: loading || waitingReaderPayment,
        onClick: onBack,
        children: "Retour"
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)("ul", {
      className: "cart-validation__list",
      children: cart.map(item => /*#__PURE__*/(0, _jsxRuntime.jsxs)("li", {
        className: "cart-validation__item",
        children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
          className: "cart-validation__item-info",
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
            className: "cart-validation__item-name",
            children: formatCartLineName(item)
          }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("span", {
            className: "cart-validation__item-detail",
            children: [formatPrice(item.unitPrice), " \xD7 ", item.quantity]
          })]
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
          className: "cart-validation__item-total",
          children: formatPrice(item.unitPrice * item.quantity)
        })]
      }, item.lineKey))
    }), /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: "cart-validation__total-row",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        className: "cart-validation__total-label",
        children: labels.total_prefix || 'Total'
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
        className: "cart-validation__total-value",
        children: formatPrice(cartTotal)
      })]
    }), availableMethods.length > 0 && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: "cart-validation__payment-methods",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "cart-validation__payment-label",
        children: "Moyen de paiement"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        className: "cart-validation__payment-options",
        children: availableMethods.map(method => /*#__PURE__*/(0, _jsxRuntime.jsxs)("label", {
          className: "cart-validation__payment-option",
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("input", {
            type: "radio",
            name: "nc-payment",
            value: method,
            checked: paymentMethod === method,
            disabled: loading,
            onChange: () => setPaymentMethod(method)
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)("span", {
            children: _newcactusApi.PAYMENT_METHOD_LABELS[method] || method
          })]
        }, method))
      })]
    }), needsPhone(paymentMethod) && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: "cart-validation__phone",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("label", {
        htmlFor: "nc-phone",
        children: "Num\xE9ro de t\xE9l\xE9phone"
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("input", {
        id: "nc-phone",
        type: "tel",
        className: "cart-validation__phone-input",
        placeholder: paymentMethod === 'momo' ? '0788123456' : '0730123456',
        value: phone,
        disabled: loading,
        onChange: e => setPhone(e.target.value)
      })]
    }), waitingReaderPayment && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      className: "cart-validation__waiting",
      role: "status",
      children: "Suivez les instructions sur le terminal WisePad\u2026"
    }), waitingMessage && !waitingReaderPayment && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      className: "cart-validation__waiting",
      role: "status",
      children: waitingMessage
    }), checkoutUrl && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: "cart-validation__qrcode-section",
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
        className: "cart-validation__qrcode-label",
        children: labels.qr_code_title || 'Scannez le QR code pour payer'
      }), /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        className: "cart-validation__qrcode-wrap",
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)("img", {
          src: `https://api.qrserver.com/v1/create-qr-code/?size=${QR_CODE_SIZE}x${QR_CODE_SIZE}&data=${encodeURIComponent(checkoutUrl)}&bgcolor=ffffff&color=1a1a2e`,
          alt: "QR Code paiement",
          className: "cart-validation__qrcode",
          width: QR_CODE_SIZE,
          height: QR_CODE_SIZE
        })
      })]
    }), error && /*#__PURE__*/(0, _jsxRuntime.jsx)("p", {
      className: "cart-validation__error",
      role: "alert",
      children: error
    }), !checkoutUrl && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      className: "cart-validation__actions",
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
        type: "button",
        className: "cart-validation__confirm",
        disabled: loading || waitingReaderPayment || !projectId,
        onClick: handleConfirmAndPay,
        children: waitingReaderPayment ? 'Paiement en cours sur le WisePad…' : loading ? 'Paiement en cours…' : 'Confirmer et payer'
      })
    })]
  });
}